// LCP — the one clock every report and audit-package PDF prints.
//
// Product owner, 2026-11-01 (Jams 9b489c14, fded7fbe), signing off inspection
// 2026-000771: the same document printed three clocks. Footers said "Submitted:
// Oct 25, 2026" with no time, the addendum pages and the Workflow/Approval
// record said "2026-11-01 23:20 UTC", and the tag histories said "10/21/2026,
// 4:17:37 PM" in whatever zone the browser happened to be in. He asked for one:
// the time zone of the person generating the document, with a time on anything
// somebody submitted, created or signed.
//
// So every printed moment goes through here, as "Nov 1, 2026, 6:20 PM EST":
// the generating user's own zone (My settings), else the browser's, else UTC,
// and always the zone's abbreviation, so a reader in another zone is never left
// guessing — and so a report spanning a daylight-saving change says which side
// of it each row sits on.
//
// A CALENDAR date (the inspection date is one: the form holds YYYY-MM-DD and the
// server stores it as midnight UTC) is never given a time. Printing that
// midnight in someone's zone would invent a moment nobody recorded, and west of
// UTC it would land on the previous day.
//
// Pure apart from the module-level user zone, which App.js keeps in step with
// the signed-in user; every formatter also takes the zone explicitly. Output is
// plain ASCII built from parts — no locale spacing (newer ICU puts U+202F before
// "PM", which the PDFs' standard fonts cannot encode).

export const REPORT_FALLBACK_ZONE = "UTC";

const BARE_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;
// An ISO date-time with no offset. The servers write UTC; read it as UTC rather
// than letting Date guess the browser's zone.
const NO_OFFSET = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatters = new Map();
const formatterFor = (zone, kind) => {
  const key = `${zone}|${kind}`;
  if (!formatters.has(key)) {
    const opts =
      kind === "parts"
        ? {
            timeZone: zone,
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
            timeZoneName: "short",
          }
        : { timeZone: zone, timeZoneName: kind };
    formatters.set(key, new Intl.DateTimeFormat("en-US", opts));
  }
  return formatters.get(key);
};

/** True for an IANA zone this browser can format in ("America/New_York", "UTC"). */
export const isValidTimeZone = (zone) => {
  if (!zone || typeof zone !== "string") return false;
  try {
    formatterFor(zone, "parts");
    return true;
  } catch (_e) {
    return false;
  }
};

export const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (_e) {
    return null;
  }
};

/** The zone to print in: the preferred one if usable, else the browser's, else UTC. */
export const resolveReportZone = (preferred) =>
  [preferred, browserTimeZone()].find(isValidTimeZone) || REPORT_FALLBACK_ZONE;

// What flex2-api stamps on a new account before anybody has chosen a zone
// (and answers when the sign-in's IP lookup finds nothing). It means "not set":
// such a user's documents print in their browser's zone, not in UTC, and the
// My settings picker shows no zone chosen yet. Mirrors flex2-api LoginTimezone.
export const UNSET_USER_ZONE = "Etc/GMT";

/** True when a profile zone is missing, unusable, or the not-chosen-yet placeholder. */
export const isUnsetUserZone = (zone) =>
  !isValidTimeZone(zone) || String(zone).toLowerCase() === UNSET_USER_ZONE.toLowerCase();

let userZone = null;

/** Called with the signed-in user's profile zone whenever it changes (App.js). */
export const setReportUserZone = (zone) => {
  userZone = isUnsetUserZone(zone) ? null : zone;
};

/** The zone a document generated right now prints in. */
export const reportZone = () => resolveReportZone(userZone);

const toDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  let d;
  if (value instanceof Date) d = value;
  else if (typeof value === "number") d = new Date(value);
  else {
    const s = String(value).trim();
    d = new Date(NO_OFFSET.test(s) ? `${s.replace(" ", "T")}Z` : s);
  }
  return Number.isNaN(d.getTime()) ? null : d;
};

const isBareDate = (value) => typeof value === "string" && BARE_DATE.test(value.trim());

const partsIn = (d, zone) => {
  const out = {};
  formatterFor(resolveReportZone(zone), "parts")
    .formatToParts(d)
    .forEach((p) => {
      out[p.type] = p.value;
    });
  return out;
};

const datePart = (p) => `${MONTHS[Number(p.month) - 1]} ${Number(p.day)}, ${p.year}`;
const timePart = (p) =>
  `${Number(p.hour)}:${p.minute} ${String(p.dayPeriod).toUpperCase()} ${p.timeZoneName}`;

/**
 * A calendar date as printed: "2026-10-21" (or the midnight-UTC timestamp the
 * server stores for one) → "Oct 21, 2026". No zone is applied — it is a date,
 * not a moment. "" when missing or unreadable.
 */
export const formatCalendarDate = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const s = value instanceof Date ? value.toISOString() : String(value).trim();
  const m = DATE_PREFIX.exec(s);
  if (!m) return "";
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return "";
  return `${MONTHS[month - 1]} ${day}, ${m[1]}`;
};

/**
 * A moment, with its time and zone: "Nov 1, 2026, 6:20 PM EST". A bare
 * calendar date prints as the date alone. "" when missing or unreadable.
 */
export const formatReportDateTime = (value, zone = reportZone()) => {
  if (isBareDate(value)) return formatCalendarDate(value);
  const d = toDate(value);
  if (!d) return "";
  const p = partsIn(d, zone);
  return `${datePart(p)}, ${timePart(p)}`;
};

/** The date a moment fell on in that zone: "Nov 1, 2026". "" when missing. */
export const formatReportDate = (value, zone = reportZone()) => {
  if (isBareDate(value)) return formatCalendarDate(value);
  const d = toDate(value);
  return d ? datePart(partsIn(d, zone)) : "";
};

/** The time alone, with its zone: "6:20 PM EST". "" for a bare date or missing. */
export const formatReportTime = (value, zone = reportZone()) => {
  if (isBareDate(value)) return "";
  const d = toDate(value);
  return d ? timePart(partsIn(d, zone)) : "";
};

/**
 * The zone's everyday name for a column header: "Eastern Time" (style "long")
 * or "ET" ("short"). Falls back to the IANA id where the browser has no
 * generic names.
 */
export const reportZoneName = (zone = reportZone(), style = "long", at = new Date()) => {
  const z = resolveReportZone(zone);
  try {
    const kind = style === "short" ? "shortGeneric" : "longGeneric";
    const part = formatterFor(z, kind)
      .formatToParts(at)
      .find((p) => p.type === "timeZoneName");
    return part?.value || z;
  } catch (_e) {
    return z;
  }
};
